// Service long-form copy, split out of services.ts so that route head() metadata
// does not pull the full body text into the critical client chunk.
import type { ServiceSection } from "./types";
import { REVIEW_COUNT_TEXT } from "@/lib/booking";

export const serviceSectionsBySlug: Record<string, ServiceSection[]> = {
  "herrklippning": [
      {
        heading: "Detta ingår i en herrklippning",
        items: [
          { title: "Rådgivning och stilanalys", text: "Vi börjar alltid med en kort konsultation där vi går igenom hur du vill se ut, hur din vardag ser ut och hur mycket tid du vill lägga på styling." },
          { title: "Klippning med precision", text: "Utförs med noggrann teknik och fokus på struktur, volym och form. Vi kombinerar sax och maskin för en jämn och balanserad klippning." },
          { title: "Form och konturer", text: "Nacke, polisonger och konturer runt öron och hårfäste får en ren och tydlig form." },
          { title: "Styling och finish", text: "Vi stylar håret med produkter som passar din hårtyp och visar hur du enkelt lägger frisyren hemma." },
        ],
      },
      {
        heading: "Vilken herrfrisyr passar dig?",
        items: [
          { title: "Klassisk herrklippning", text: "Tidlös herrfrisyr med ren form, tydliga linjer och kontrollerad struktur. Fungerar lika bra på jobbet som i vardagen." },
          { title: "Modern hårtrend för män", text: "Textur, rörelse och volym. Längre toppar och mjuka övergångar för en avslappnad men kontrollerad stil." },
          { title: "Kort clean cut", text: "Alltid fräsch, minimal styling, skarp form." },
          { title: "Texturerad frisyr", text: "Olika längder och lager ger mer liv och rörelse. Bra för tjockt hår." },
          { title: "Herrklippning efter hårtyp", text: "Vi anpassar klippningen efter rakt, lockigt, vågigt, tjockt eller tunt hår." },
        ],
      },
      {
        heading: "Varför en professionell klippning gör skillnad",
        items: [
          { text: "Frisyren håller formen längre." },
          { text: "Anpassad efter ansiktsform och hårtyp." },
          { text: "Enklare att styla i vardagen." },
          { text: "Personlig rådgivning kring produkter och grooming." },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "En herrklippning tar vanligtvis 30–45 minuter. Pris: 450 kr. Samma pris gäller för fade och taper fade.",
      },
  ],
  "taper-fade": [
      {
        heading: "Detta ingår i en taper fade",
        items: [
          { title: "Kort konsultation", text: "Genomgång av önskad nivå (low/mid/high) och hur du brukar styla håret." },
          { title: "Maskin- och saxarbete med fokus på övergångar", text: "Metodisk precision för mjuk och jämn övergång." },
          { title: "Rena linjer och tydliga detaljer", text: "Linjer runt öron, nacke och polisonger formas skarpt." },
          { title: "Styling och avslutande finish", text: "Vi visar hur du enkelt stylar faden hemma." },
        ],
      },
      {
        heading: "Vilken taper fade passar dig?",
        items: [
          { title: "Low Taper Fade", text: "Mjuk övergång precis ovanför öronen. Diskret och stilren." },
          { title: "Mid Taper Fade", text: "Lite mer kontrast. Bra balans mellan stilrent och modernt." },
          { title: "High Taper Fade", text: "Övergång högre upp för en mer markerad, trendig look." },
          { title: "Textured Taper Fade", text: "Kombineras med texturering på toppen för mer volym." },
          { title: "Taper Fade anpassad efter hårtyp", text: "Fungerar på rakt, lockigt, vågigt och tjockt hår." },
        ],
      },
      {
        heading: "Varför en professionell taper fade gör skillnad",
        intro: "Övergångarna blir jämna, rena och symmetriska. Klippningen anpassas efter huvudform, hårväxt och din stil — och frisyren växer ut mjukt och håller formen längre.",
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "En taper fade tar vanligtvis 25–40 minuter. Pris: 450 kr.",
      },
  ],
  "barnklippning": [
      {
        heading: "Första gången barnet klipper sig",
        intro: "Säg till när du bokar eller när ni kommer. Barberaren visar maskinen och saxen innan de används och förklarar vad som ska hända. Många föräldrar börjar med att barnet får sitta i förälderns knä. Det går bra att ta en paus mitt i.",
      },
      {
        heading: "Om barnet är blygt eller har svårt att sitta still",
        intro: "Berätta det på plats. Barberaren sänker tempot och tar fler pauser. Tidig förmiddag på vardagar är lugnast i salongen, med minst folk och minst ljud, vilket brukar göra skillnad för barn som tycker att det är mycket intryck.",
      },
      {
        heading: "Om det inte går",
        intro: "Ibland vill barnet inte. Blir barnet riktigt ledset avbryter vi klippningen. Det är i första hand för barnets skull, och för att salongen är ett litet rum där alla hör varandra. Innan dess provar barberaren att ta en paus, byta plats eller låta dig hålla barnet i knät. Går det ändå inte bokar vi hellre om än tvingar fram ett resultat. Hur vi gör med betalningen beror på hur långt vi hunnit, det tar barberaren med dig på plats. Många barn som inte vill första gången klarar det andra gången.",
      },
      {
        heading: "Kom in och titta först",
        intro: "Är du osäker på om barnet är redo får ni gärna komma in och titta utan att boka. Barnet får se salongen, höra ljudet och träffa barberaren, och sedan bestämmer ni er. Det kostar ingenting och tar ett par minuter.",
      },
      {
        heading: "Säg vad du vill ha, även för ett barn",
        intro: "Beskriv längden i centimeter eller ta med en bild. Det är den vanligaste orsaken till att en förälder blir missnöjd: att önskemålet uppfattades annorlunda. Barberaren frågar innan klippningen börjar, men ta gärna upp det själv också.",
      },
      {
        heading: "Varför välja Tailor's Barber för barnklippning?",
        items: [
          { title: "Erfaren barnfrisör", text: "Barberaren förklarar varje moment innan det görs och svarar på barnets frågor under klippningen." },
          { title: "Pauser när barnet behöver", text: "Barberaren stannar upp om barnet vill vila, titta i spegeln eller sitta i förälderns knä en stund." },
          { title: "Perfekt läge i city", text: "Åhléns City ligger en minut från T-Centralen och nås med kollektivtrafik och barnvagn." },
          { title: "Barberaren pratar med barnet", text: "Frågor om längd och stil ställs till barnet också, inte bara till föräldern." },
          { title: "Frisyrer som fungerar i vardagen", text: "Barberaren frågar hur håret sköts hemma och väljer längd därefter." },
        ],
      },
      {
        heading: "Vilken frisyr passar ditt barn?",
        items: [
          { title: "Klassisk och lättskött", text: "Rena linjer och lagom längd. Tidlöst och enkelt att sköta." },
          { title: "Modern look med mjuka övergångar", text: "Lätt textur och naturligt fall." },
          { title: "Kort & clean cut", text: "Smidigt och snabbt, minimal underhåll." },
          { title: "Längre frisyr med naturlig textur", text: "Mjuka lager för barn med lockigt eller tjockt hår." },
        ],
      },
      {
        heading: "Detta ingår i en barnklippning",
        items: [
          { text: "Barberaren visar maskin och sax innan de används" },
          { text: "Frågor om önskad längd innan klippningen börjar" },
          { text: "Pauser om barnet behöver vila eller byta plats" },
          { text: "Form och finish som håller" },
          { text: "Lätt styling och tips för hemma" },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "Barnklippning tar omkring 25 till 30 minuter. Pris: 380 kr (barn upp till 10 år). Ingen taper fade eller skin fade ingår.",
      },
  ],
  "barnklippning-taby": [
      {
        heading: "Första gången barnet klipper sig",
        intro: "Säg till när du bokar eller när ni kommer. Barberaren visar maskinen och saxen innan de används och förklarar vad som ska hända. Många föräldrar börjar med att barnet får sitta i förälderns knä. Det går bra att ta en paus mitt i.",
      },
      {
        heading: "Om barnet är blygt eller har svårt att sitta still",
        intro: "Berätta det på plats. Barberaren sänker tempot och tar fler pauser. Tidig förmiddag på vardagar är lugnast i salongen, med minst folk och minst ljud, vilket brukar göra skillnad för barn som tycker att det är mycket intryck.",
      },
      {
        heading: "Om det inte går",
        intro: "Ibland vill barnet inte. Blir barnet riktigt ledset avbryter vi klippningen. Det är i första hand för barnets skull, och för att salongen är ett litet rum där alla hör varandra. Innan dess provar barberaren att ta en paus, byta plats eller låta dig hålla barnet i knät. Går det ändå inte bokar vi hellre om än tvingar fram ett resultat. Hur vi gör med betalningen beror på hur långt vi hunnit, det tar barberaren med dig på plats. Många barn som inte vill första gången klarar det andra gången.",
      },
      {
        heading: "Kom in och titta först",
        intro: "Är du osäker på om barnet är redo får ni gärna komma in och titta utan att boka. Barnet får se salongen, höra ljudet och träffa barberaren, och sedan bestämmer ni er. Det kostar ingenting och tar ett par minuter.",
      },
      {
        heading: "Säg vad du vill ha, även för ett barn",
        intro: "Beskriv längden i centimeter eller ta med en bild. Det är den vanligaste orsaken till att en förälder blir missnöjd: att önskemålet uppfattades annorlunda. Barberaren frågar innan klippningen börjar, men ta gärna upp det själv också.",
      },
      {
        heading: "Varför välja Tailor's Barber för barnklippning i Täby?",
        items: [
          { title: "Erfarna barnfrisörer i Täby", text: "Barberaren förklarar varje moment innan det görs och svarar på barnets frågor under klippningen." },
          { title: "Pauser när barnet behöver", text: "Barberaren stannar upp om barnet vill vila, titta i spegeln eller sitta i förälderns knä en stund. Tidig förmiddag på vardagar är lugnast i salongen." },
          { title: "Smidigt läge i Täby Centrum", text: "Westfield Täby Centrum har stor parkeringsyta, och pendeltåg och buss stannar direkt utanför." },
          { title: "Barberaren pratar med barnet", text: "Frågor om längd och stil ställs till barnet också, inte bara till föräldern, och anpassas efter ålder." },
          { title: "Frisyrer som fungerar i vardagen", text: "Barberaren frågar hur håret sköts hemma och väljer en längd som är lätt att hålla efter." },
        ],
      },
      {
        heading: "Vilken frisyr passar ditt barn?",
        items: [
          { title: "Klassisk och lättskött", text: "Rena linjer och lagom längd. Tidlöst och enkelt att sköta i vardagen." },
          { title: "Modern look med mjuka övergångar", text: "Lätt textur och naturligt fall som inte kräver mycket styling." },
          { title: "Kort & clean cut", text: "Smidigt och snabbt, minimal underhåll och alltid fräscht." },
          { title: "Längre frisyr med naturlig textur", text: "Mjuka lager för barn med lockigt eller tjockt hår." },
        ],
      },
      {
        heading: "Detta ingår i en barnklippning",
        items: [
          { text: "Barberaren visar maskin och sax innan de används" },
          { text: "Frågor om önskad längd innan klippningen börjar" },
          { text: "Pauser om barnet behöver vila eller byta plats" },
          { text: "Form och finish som håller" },
          { text: "Lätt styling och tips för hemma" },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "Barnklippning tar omkring 25 till 30 minuter. Pris: 380 kr (barn upp till 10 år). Taper fade och skin fade ingår inte.",
      },
  ],
  "barnklippning-arninge": [
      {
        heading: "Första gången barnet klipper sig",
        intro: "Säg till när du bokar eller när ni kommer. Barberaren visar maskinen och saxen innan de används och förklarar vad som ska hända. Många föräldrar börjar med att barnet får sitta i förälderns knä. Det går bra att ta en paus mitt i.",
      },
      {
        heading: "Om barnet är blygt eller har svårt att sitta still",
        intro: "Berätta det på plats. Barberaren sänker tempot och tar fler pauser. Tidig förmiddag på vardagar är lugnast i salongen, med minst folk och minst ljud, vilket brukar göra skillnad för barn som tycker att det är mycket intryck.",
      },
      {
        heading: "Om det inte går",
        intro: "Ibland vill barnet inte. Blir barnet riktigt ledset avbryter vi klippningen. Det är i första hand för barnets skull, och för att salongen är ett litet rum där alla hör varandra. Innan dess provar barberaren att ta en paus, byta plats eller låta dig hålla barnet i knät. Går det ändå inte bokar vi hellre om än tvingar fram ett resultat. Hur vi gör med betalningen beror på hur långt vi hunnit, det tar barberaren med dig på plats. Många barn som inte vill första gången klarar det andra gången.",
      },
      {
        heading: "Kom in och titta först",
        intro: "Är du osäker på om barnet är redo får ni gärna komma in och titta utan att boka. Barnet får se salongen, höra ljudet och träffa barberaren, och sedan bestämmer ni er. Det kostar ingenting och tar ett par minuter.",
      },
      {
        heading: "Säg vad du vill ha, även för ett barn",
        intro: "Beskriv längden i centimeter eller ta med en bild. Det är den vanligaste orsaken till att en förälder blir missnöjd: att önskemålet uppfattades annorlunda. Barberaren frågar innan klippningen börjar, men ta gärna upp det själv också.",
      },
      {
        heading: "Varför välja Tailor's Barber för barnklippning i Arninge?",
        items: [
          { title: "Erfarna barnfrisörer i Arninge", text: "Barberaren förklarar varje moment innan det görs och svarar på barnets frågor under klippningen." },
          { title: "Pauser när barnet behöver", text: "Barberaren stannar upp om barnet vill vila, titta i spegeln eller sitta i förälderns knä en stund. Tidig förmiddag på vardagar är lugnast i salongen." },
          { title: "Smidigt läge vid ICA Maxi Arninge", text: "Stor gratis parkering direkt vid salongen. Enkelt att kombinera med andra ärenden i Arninge Centrum." },
          { title: "Barberaren pratar med barnet", text: "Frågor om längd och stil ställs till barnet också, inte bara till föräldern, och anpassas efter ålder." },
          { title: "Lättskötta frisyrer för barn", text: "Barberaren frågar hur håret sköts hemma och väljer längd därefter." },
        ],
      },
      {
        heading: "Vilken frisyr passar ditt barn?",
        items: [
          { title: "Klassisk och lättskött", text: "Rena linjer och lagom längd. Tidlöst och enkelt att sköta." },
          { title: "Modern look med mjuka övergångar", text: "Lätt textur och naturligt fall." },
          { title: "Kort & clean cut", text: "Smidigt och snabbt, minimal underhåll." },
          { title: "Längre frisyr med naturlig textur", text: "Mjuka lager för barn med lockigt eller tjockt hår." },
        ],
      },
      {
        heading: "Detta ingår i en barnklippning",
        items: [
          { text: "Barberaren visar maskin och sax innan de används" },
          { text: "Frågor om önskad längd innan klippningen börjar" },
          { text: "Pauser om barnet behöver vila eller byta plats" },
          { text: "Form och finish som håller" },
          { text: "Lätt styling och tips för hemma" },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "Barnklippning tar omkring 25 till 30 minuter. Pris: 380 kr (barn upp till 10 år). Taper fade och skin fade ingår inte.",
      },
  ],
  "barnklippning-akersberga": [
      {
        heading: "Första gången barnet klipper sig",
        intro: "Säg till när du bokar eller när ni kommer. Barberaren visar maskinen och saxen innan de används och förklarar vad som ska hända. Många föräldrar börjar med att barnet får sitta i förälderns knä. Det går bra att ta en paus mitt i.",
      },
      {
        heading: "Om barnet är blygt eller har svårt att sitta still",
        intro: "Berätta det på plats. Barberaren sänker tempot och tar fler pauser. Tidig förmiddag på vardagar är lugnast i salongen, med minst folk och minst ljud, vilket brukar göra skillnad för barn som tycker att det är mycket intryck.",
      },
      {
        heading: "Om det inte går",
        intro: "Ibland vill barnet inte. Blir barnet riktigt ledset avbryter vi klippningen. Det är i första hand för barnets skull, och för att salongen är ett litet rum där alla hör varandra. Innan dess provar barberaren att ta en paus, byta plats eller låta dig hålla barnet i knät. Går det ändå inte bokar vi hellre om än tvingar fram ett resultat. Hur vi gör med betalningen beror på hur långt vi hunnit, det tar barberaren med dig på plats. Många barn som inte vill första gången klarar det andra gången.",
      },
      {
        heading: "Kom in och titta först",
        intro: "Är du osäker på om barnet är redo får ni gärna komma in och titta utan att boka. Barnet får se salongen, höra ljudet och träffa barberaren, och sedan bestämmer ni er. Det kostar ingenting och tar ett par minuter.",
      },
      {
        heading: "Säg vad du vill ha, även för ett barn",
        intro: "Beskriv längden i centimeter eller ta med en bild. Det är den vanligaste orsaken till att en förälder blir missnöjd: att önskemålet uppfattades annorlunda. Barberaren frågar innan klippningen börjar, men ta gärna upp det själv också.",
      },
      {
        heading: "Varför välja Tailor's Barber för barnklippning i Åkersberga?",
        items: [
          { title: "Erfarna barnfrisörer i Åkersberga", text: "Barberaren förklarar varje moment innan det görs och svarar på barnets frågor under klippningen." },
          { title: "Anpassat i barnets tempo", text: "Barberaren visar verktygen innan de används och stannar upp om barnet vill vila eller sitta i förälderns knä en stund." },
          { title: "Centralt i Åkersberga", text: "Salongen ligger i Åkersberga Centrum, så klippningen går att kombinera med andra ärenden." },
          { title: "Barberaren pratar med barnet", text: "Frågor om längd och stil ställs till barnet också, inte bara till föräldern, och anpassas efter ålder." },
          { title: "Lättskötta frisyrer som håller", text: "Barberaren frågar hur håret sköts hemma och väljer en längd som är lätt att hålla efter." },
        ],
      },
      {
        heading: "Vilken frisyr passar ditt barn?",
        items: [
          { title: "Klassisk och lättskött", text: "Rena linjer och lagom längd. Tidlöst och enkelt att sköta." },
          { title: "Modern look med mjuka övergångar", text: "Lätt textur och naturligt fall för en fräsch vardagslook." },
          { title: "Kort & clean cut", text: "Minimal underhåll och alltid fräscht." },
          { title: "Längre frisyr med naturlig textur", text: "Mjuka lager för barn med lockigt eller tjockt hår." },
        ],
      },
      {
        heading: "Detta ingår i en barnklippning",
        items: [
          { text: "Barberaren visar maskin och sax innan de används" },
          { text: "Frågor om önskad längd innan klippningen börjar" },
          { text: "Pauser om barnet behöver vila eller byta plats" },
          { text: "Form och finish som håller" },
          { text: "Lätt styling och tips för hemma" },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "Barnklippning tar omkring 25 till 30 minuter. Pris: 380 kr (barn upp till 10 år). Taper fade och skin fade ingår inte.",
      },
  ],
  "herrklippning-taby": [
      {
        heading: "Varför välja Tailor's Barber för herrklippning i Täby?",
        items: [
          {
            title: "Flaggskeppssalongen i Westfield Täby Centrum",
            text: "Vår Täby-salong är Tailor's Barbers mest erfarna och mest bokade — det är hit vi lägger vår starkaste personalstyrka. Du möter barberare med lång erfarenhet av allt från klassiska herrklippningar till avancerade fade-tekniker.",
          },
          {
            title: "Centralt i Täby Centrum med enkel access",
            text: "Vi sitter inne i Westfield Täby Centrum, Biblioteksgången 1. Täby Centrum nås enkelt med Roslagsbanan till Täby Kyrkby eller bil via E18 — stort parkeringsgarage direkt i centret.",
          },
          {
            title: "Drop-in och tidsbokning",
            text: "Vi tar emot drop-in i mån av tid — passa på när du ändå handlar i centret. Vill du garantera en tid bokar du enkelt via BokaDirekt. Öppet måndag–fredag 10–20, lördag 10–18, söndag 10–18.",
          },
          {
            title: "Alla tjänster på ett ställe",
            text: "Herrklippning, skin fade, taper fade, skäggtrimning och barnklippning — allt under ett tak. Boka herr och skägg i ett paket och spara tid.",
          },
        ],
      },
  ],
  "herrklippning-arninge": [
      {
        heading: "Herrklippning i Arninge — bekvämt och lättillgängligt",
        items: [
          {
            title: "Inne i ICA Maxi Arninge-området",
            text: "Vår salong ligger vid Kundvägen 1D i Arninge Centrum, omgiven av ICA Maxi, IKEA och Bauhaus. Perfekt om du ändå har ärenden i området — klipp håret medan du handlar.",
          },
          {
            title: "Gratis parkering och enkel access",
            text: "Arninge är ett bilburget handelsområde med generöst med gratis parkering direkt utanför. Nås även med buss från Täby och Vallentuna.",
          },
          {
            title: "Professionella barberare — samma standard som i city",
            text: "Samma utbildade barberare, samma teknik och samma krav på resultat som i alla Tailor's Barber-salonger. Du behöver inte åka in till Stockholm för en professionell herrklippning.",
          },
          {
            title: "Drop-in välkommen",
            text: "Vi tar emot drop-in i mån av tid måndag–fredag 10–19, lördag–söndag 10–18. Vill du säkra en specifik tid bokar du via BokaDirekt.",
          },
        ],
      },
  ],
  "herrklippning-akersberga": [
      {
        heading: "Herrklippning Åkersberga — Österåkers enda barbershop",
        items: [
          {
            title: "Specialiserade barberare — inte en vanlig frisör",
            text: "Till skillnad från generella hårsalonger i Åkersberga är Tailor's Barber en dedikerad barbershop med barberare specialiserade på herrklippning, fade och skägg. Det syns i resultatet.",
          },
          {
            title: "Centralt i ICA Maxi Åkersberga",
            text: "Vi finns på Långhundravägen 9 inne i ICA Maxi Åkersberga. Gott om gratis parkering och enkelt nåbart från hela Österåker. Ostkustbanan med station i Åkersberga centrum är en kort promenad bort.",
          },
          {
            title: "Skin fade och taper fade i Åkersberga",
            text: "Skin fade och taper fade är några av de mest efterfrågade tjänsterna i Åkersberga. Våra barberare arbetar med precision och använder maskin och rakkniv för ett skarpt, rent resultat.",
          },
          {
            title: "Drop-in och bokning online",
            text: "Passa på att drop-ina när du ändå handlar på ICA Maxi — vi tar emot drop-in i mån av tid. Tidsbokning via BokaDirekt garanterar din plats.",
          },
        ],
      },
  ],
  "skin-fade-taby": [
      {
        heading: "Vad är en skin fade?",
        intro: "Skin fade innebär att håret gradvis övergår till ren hud vid tinningar, nacke och sidor. Det är den skarpaste formen av fade och kräver precision med maskin och rakkniv.",
        items: [
          { title: "Hög skin fade", text: "Övergången börjar högt upp på huvudet — ger ett dramatiskt, modernt uttryck." },
          { title: "Mid skin fade", text: "Den vanligaste varianten. Balanserad övergång som passar de flesta hårtyper och ansiktsformer." },
          { title: "Low skin fade", text: "Subtil övergång som börjar lågt — bra för dig som vill ha en diskretare look." },
          { title: "Drop fade", text: "Övergången droppar bakom örat — populärt val för ett kontemporärt uttryck." },
        ],
      },
      {
        heading: "Varför Tailor's Barber Täby?",
        intro: "Vår salong i Westfield Täby Centrum är vår mest bokade salong. Skin fade är en spetskompetens hos våra barberare — du ser det i precisionen vid varje besök.",
        items: [
          { title: "Erfarna barberare", text: "Täby-teamet utför skin fade dagligen och håller tekniken skarp." },
          { title: "Rätt verktyg", text: "Vi använder professionella maskiner och rakkniv för en perfekt slät övergång till hud." },
          { title: "Snabb bokning", text: "Boka online på BokaDirekt — välj din barberare och tid direkt." },
          { title: "Westfield Täby Centrum", text: "Centralt läge med gott om parkering. Enkelt att kombinera med shopping." },
        ],
      },
  ],
  "skin-fade-arninge": [
      {
        heading: "Skin fade i Arninge",
        intro: "Arninge Centrum drar besökare från ett brett upptagningsområde. Vi är den enda barbershopen i området som specialiserar sig på fade-tekniker med maskin och rakkniv.",
        items: [
          { title: "Noll konkurrens på fade", text: "Inga andra salonger i Arninge erbjuder skin fade på vår nivå — vi är det naturliga valet." },
          { title: "Smidigt läge", text: "ICA Maxi-komplexet i Arninge — enkelt att nå med bil från Täby, Vallentuna och Rimbo-hållet." },
          { title: "Precision varje gång", text: "Vårt team utför fade med noggrannhet och tar sig tid att göra övergångarna perfekta." },
          { title: "Boka enkelt", text: "Online-bokning via BokaDirekt. Välj tid och barberare på några sekunder." },
        ],
      },
      {
        heading: "Vilken fade passar dig?",
        intro: "Skin fade finns i flera varianter. Berätta för din barberare vad du vill ha — vi hjälper dig hitta rätt om du är osäker.",
        items: [
          { title: "Low skin fade", text: "Subtil och diskret — passar dig som vill ha en fade utan för dramatisk effekt." },
          { title: "Mid skin fade", text: "Den vanligaste — balanserad och modern, passar de flesta." },
          { title: "Hög skin fade", text: "Tydlig kontrast och modernt uttryck — populärt val för crop och modern textur." },
          { title: "Kombinera med skägg", text: "Lägg till skäggtrimning för ett komplett resultat — vi formar skägget i linje med faden." },
        ],
      },
  ],
  "skin-fade-akersberga": [
      {
        heading: "Skin fade i Åkersberga",
        intro: "Vi är den enda barbershopen i Österåker med dedikerad fade-kompetens. Inga generella hårsalonger — ren barbering med rätt verktyg och teknik.",
        items: [
          { title: "Enda barbershopen i Österåker", text: "Ingen annan salong i Åkersberga erbjuder skin fade med professionell fade-teknik." },
          { title: "Samma standard som city", text: "Identisk utrustning och utbildning som våra salonger i Stockholm och Täby." },
          { title: "Lokalt och bekvämt", text: "Slipp resan in till stan. Vi finns i ICA Maxi-komplexet i Åkersberga." },
          { title: "Boka online", text: "Snabb bokning via BokaDirekt — välj barberare och tid direkt." },
        ],
      },
      {
        heading: "Populärt i Åkersberga",
        intro: "Skin fade kombineras ofta med andra tjänster. Här är vad kunderna i Åkersberga bokar mest.",
        items: [
          { title: "Skin fade + toppklipp", text: "Den klassiska kombinationen — fade på sidorna och anpassad längd på toppen." },
          { title: "Skin fade + skäggtrimning", text: "Komplett look — vi formar skägg och fade i linje med varandra." },
          { title: "Hög skin fade", text: "Dramatisk och modern — tydlig kontrast som syns direkt." },
          { title: "Low skin fade", text: "Diskret men elegant — perfekt för dig som vill ha subtil fade." },
        ],
      },
  ],
  "herrklippning-skaggtrimning": [
      {
        heading: "Detta ingår i herrklippning & skäggtrimning",
        items: [
          { title: "Konsultation", text: "Vi går igenom både hår och skägg — stil, längd, form och önskemål." },
          { title: "Herrklippning med precision", text: "Klassiskt eller modernt, med anpassad längd, form och volym." },
          { title: "Skäggtrimning och formning", text: "Skägget formas med sax, maskin och kniv för en balanserad och ren look." },
          { title: "Konturer och detaljer", text: "Kinder, hals och polisonger rakas för att göra helheten skarpare." },
          { title: "Styling och skäggvård", text: "Produkter för både hår och skägg — fräsch finish och tips för hemma." },
        ],
      },
      {
        heading: "Vilken stil passar dig?",
        items: [
          { title: "Klassisk klippning & välformat skägg", text: "Tidlöst och välvårdat — funkar i alla sammanhang." },
          { title: "Modern look med textur & definierade linjer", text: "Trendig men tydlig — bra val för dig som vill sticka ut lite." },
          { title: "Kort clean cut + tight skäggtrim", text: "Alltid fräscht och minimalt underhåll." },
          { title: "Texturerad frisyr & naturligt formade skägglinjer", text: "Personligt och naturligt uttryck." },
        ],
      },
      {
        heading: "Vem passar denna behandling för?",
        intro:
          "Perfekt för dig som vill ha allt gjort vid samma besök — sparar tid och ger en genomarbetad helhet där hår och skägg matchar varandra.",
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "Herrklippning & Skäggtrimning tar cirka 45–60 minuter. Pris: 680 kr (paketpris).",
      },
  ],
  "skaggtrimning": [
      {
        heading: "Detta ingår i en skäggtrimning",
        items: [
          { title: "Rådgivning och stilanalys", text: "Vi går igenom vilken form som passar ditt ansikte och din skäggväxt." },
          { title: "Formning och längdjustering", text: "Sax och maskin beroende på stil och hårlängd." },
          { title: "Linjer och konturer", text: "Kinder, mustasch, käklinje och hals — skarpa och rena kanter." },
          { title: "Mustaschtrimning", text: "Mustaschen klipps och formas ihop med resten av skägget." },
          { title: "Styling och finish", text: "Produkter som mjukar upp och gör skägget lätt att sköta hemma." },
        ],
      },
      {
        heading: "Vilken skäggstil passar dig?",
        items: [
          { title: "Kort skägg", text: "Lätt att sköta, rent och fräscht utseende." },
          { title: "Längre skägg", text: "Vi formar längd och sidor så att det ser ordnat ut utan att kännas stelt." },
          { title: "Stubb", text: "Markerat uttryck utan helskägg." },
          { title: "Mustasch", text: "Formas och trimmas för rätt uttryck." },
        ],
      },
      {
        heading: "Varför en professionell skäggtrimning gör skillnad",
        items: [
          { text: "Skägget ser jämnt ut och håller formen längre." },
          { text: "Du slipper ojämna kanter." },
          { text: "Lättare att sköta hemma." },
          { text: "Stilen anpassas efter ditt ansikte." },
        ],
      },
      {
        heading: "Så går en skäggtrimning till hos oss",
        intro: "Behandlingen tar 20 till 30 minuter och följer samma upplägg på alla fyra salonger.",
        items: [
          { title: "Konsultation", text: "Barberaren går igenom hur ditt skägg växer, hur tätt det är och vilken stil du vill uppnå, markerad med tydliga linjer eller mer naturlig och fyllig." },
          { title: "Trimning i flera steg", text: "Sax och trimmer används växelvis för mjuka övergångar. Längre skägg behåller sin fyllighet men får symmetri, kortare skägg får skarpa kanter och ren struktur." },
          { title: "Rakkniv och varm handduk", text: "Hals och kindlinjer rakas rent. Det är detaljen som gör att skägget ser putsat ut i flera veckor." },
          { title: "Lugnande finish", text: "After shave balm eller olja återfuktar huden och motverkar rodnad." },
          { title: "Styling och råd", text: "Skägget stylas efter din hårtyp, från naturlig glans till matt textur, och du får råd om hur du sköter formen hemma." },
        ],
      },
      {
        heading: "Skäggtrimning hos barberare eller hemma?",
        intro: "En trimmer hemma håller längden nere, men den ser inte ditt ansikte utifrån.",
        items: [
          { text: "Symmetri: barberaren arbetar från båda sidor och ser skevheter som är omöjliga att upptäcka i spegeln." },
          { text: "Linjer: kind och halslinje sätts med rakkniv, inte med gissning. Fel halslinje är det vanligaste hemmamisstaget." },
          { text: "Form efter ansiktsform: ett runt ansikte behöver andra linjer än ett smalt, och växtriktningen styr var längden ska ligga." },
          { text: "Hållbarhet: en väl formad trimning växer ut jämnt, så du kan gå längre mellan besöken." },
        ],
      },
      {
        heading: "Skäggvård efter besöket",
        intro: "Resultatet håller längre om du sköter skägget mellan besöken. Vi säljer produkterna vi använder i salongen.",
        items: [
          { title: "Skäggolja", text: "Dagligen på fuktigt skägg, mjukar upp stråna och motverkar klåda och torr hud." },
          { title: "Skäggbalsam", text: "Ger hållning och lyster åt längre skägg, används efter oljan." },
          { title: "Kam och borste", text: "Fördelar produkten och tränar växtriktningen så att skägget lägger sig rätt." },
          { title: "Ombokning", text: "Boka nästa besök efter två till fyra veckor beroende på längd, då behåller skägget sin form." },
        ],
      },
      {
        heading: "Skäggtrimning på våra fyra salonger",
        intro: "Samma behandling och samma pris överallt. Välj den salong som ligger bäst till för dig, adresser och öppettider finns på respektive salongssida längre ner.",
        items: [
          { title: "Åhléns City", text: "Klarabergsgatan 50, plan 3, en minut från T-Centralen. Passar bra för en behandling på lunchen." },
          { title: "Täby Centrum", text: "Biblioteksgången 1, mitt i Westfield Täby Centrum, enkelt att kombinera med shopping." },
          { title: "Åkersberga", text: "Långhundravägen 9, inne i ICA Maxi med gratis parkering och buss utanför." },
          { title: "Arninge", text: "Kundvägen 1D i ICA Maxi Arninge, gott om parkering direkt utanför." },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "En skäggtrimning tar 20 till 30 minuter och kostar 380 kr. Priset inkluderar formning, konturer, mustaschtrimning och putsning. Vill du klippa dig samtidigt kostar hår och skägg 680 kr.",
      },
  ],

  "slatrakning": [
      {
        heading: "Detta ingår i en slätrakning",
        items: [
          { title: "Förberedelse med varm handduk", text: "Huden och skäggstubben mjukas upp för en skonsammare rakning och mindre irritation." },
          { title: "Rakning med rakkniv", text: "Traditionell teknik både med- och mothårs för ett nära, lent resultat." },
          { title: "Rena linjer och konturer", text: "Kinder, hals och käklinje får skarpa, definierade kanter." },
          { title: "Återfuktande finish", text: "Vi avslutar med lugnande produkter som motverkar rodnad och irritation." },
        ],
      },
      {
        heading: "När passar en slätrakning?",
        items: [
          { title: "Inför en viktig dag", text: "Bröllop, fest, intervju eller dejt — en slätrakning ger en fräsch och välvårdad känsla." },
          { title: "När du vill slippa stubb", text: "Rakkniv kommer närmare huden än maskin och ger ett längre hållbart resultat." },
          { title: "Som komplement till klippning", text: "Boka klippning och slätrakning tillsammans för en komplett makeover." },
        ],
      },
      {
        heading: "Pris och tidsåtgång",
        intro: "En slätrakning tar cirka 30 minuter. Pris: 390 kr. Inkluderar varm handduk, rakning med rakkniv och finish.",
      },
  ],
};

export const serviceDescriptionBySlug: Record<string, string[]> = {
  "herrklippning": [
      "Herrklippning Stockholm är kärnan i vårt utbud på fyra salonger. En bra herrklippning handlar om mer än att bara klippa håret. Våra barberare skapar frisyrer som passar din ansiktsform, hårtyp och personliga stil.",
      "Oavsett om du vill ha en klassisk frisyr, en modern texturerad look eller en kort, stilren klippning får du en professionell behandling där vi lägger fokus på detaljer, precision och hållbarhet. Vi ger dig även rådgivning om styling, produkter och hur du håller frisyren i form mellan besöken.",
  ],
  "herrklippning-stockholm": [
      "Letar du efter herrklippning i Stockholm? Tailor's Barber har fyra salonger i Stockholmsområdet — Täby Centrum, Åhléns City, Åkersberga och Arninge.",
      `Vi är en av Stockholms mest bokade barberkedjor med ${REVIEW_COUNT_TEXT} från Google och BokaDirekt.`,
  ],
  "taper-fade": [
      "En bra taper fade handlar om mer än att bara klippa kort på sidorna. Våra barberare jobbar med noggranna övergångar och rena linjer som passar din ansiktsform, hårtyp och personliga stil.",
      "Oavsett om du vill ha en low, mid eller high taper fade får du en klippning där vi lägger fokus på detaljer, precision och hållbarhet.",
  ],
  "fade": [
      "Fade är en av de mest populära herrfrisyrerna idag. Håret skärs ned gradvis från längden uppe på huvudet ner till skinnet vid sidor och nacke.",
      "Vi erbjuder low fade, mid fade och high fade — alla med skarpa övergångar och en finish som håller länge.",
  ],
  "har-skagg": [
      "Vår mest populära kombinerade behandling. Vi börjar med herrklippning anpassad efter ditt hår och önskemål, följt av en professionell skäggtrimning där skägget formas med sax och trimmer och linjerna markeras med kniv.",
      "Behandlingen avslutas med varm handduk för en mjuk och välvårdad finish.",
  ],
  "barnklippning": [
      "Barnklippning i Stockholm kostar 380 kr och gäller barn upp till 10 år. Vi klipper barn på alla fyra salonger, och barberaren visar verktygen innan de används och tar pauser om barnet behöver det.",
      "Du som förälder kan sitta bredvid eller ha barnet i knät. Tidig förmiddag på vardagar är lugnast i salongen. Boka online eller kom på drop-in i mån av plats.",
  ],
  "barnklippning-taby": [
      "Behöver du barnklippning i Täby? Tailor's Barber på Täby Centrum klipper barn upp till 10 år för 380 kr. Barberaren visar maskin och sax innan de används och tar pauser om barnet behöver det.",
      "Salongen ligger inne i Westfield Täby Centrum med stor parkering, och pendeltåg och buss stannar precis utanför. Du som förälder kan sitta bredvid eller ha barnet i knät, och tidig förmiddag på vardagar är lugnast i salongen.",
  ],
  "barnklippning-arninge": [
      "Tailor's Barber i Arninge Centrum, intill ICA Maxi, klipper barn upp till 10 år för 380 kr. Barberaren förklarar vad som ska hända och visar verktygen innan de används.",
      "Salongen i Arninge har generös parkering precis utanför och är enkel att nå med bil via E18. Barberaren tar pauser om barnet behöver det, och du som förälder kan sitta med hela tiden.",
  ],
  "barnklippning-akersberga": [
      "Letar du efter barnklippning i Åkersberga? Tailor's Barber i Åkersberga Centrum klipper barn upp till 10 år för 380 kr. Barberaren visar verktygen innan de används och tar pauser om barnet behöver det.",
      "Salongen ligger centralt i Åkersberga med god parkering i närheten. Barberaren frågar om önskad längd innan klippningen börjar och väljer en frisyr som passar barnets hårtyp.",
  ],
  "herrklippning-taby": [
      "Välkommen till Tailor's Barber i Täby Centrum — vår flaggskeppssalong och den mest bokade barbershopen i Stockholmsregionen. Här kombinerar vi klassisk barberstradition med moderna frisyrtekniker för ett resultat som håller.",
      "Oavsett om du vill ha en ren herrklippning, skapa volym på toppen eller hålla frisyren fräsch med en precision-trim — våra barberare i Täby levererar varje gång. Vi tar emot drop-in i mån av tid och erbjuder enkel bokning online dygnet runt.",
  ],
  "herrklippning-arninge": [
      "Tailor's Barber i Arninge finns inne i ICA Maxi Arninge vid Kundvägen 1D — ett av norra Stockholms mest trafikerade handelsområden. Här kan du kombinera din herrklippning med ett snabbt ärende hos IKEA, Bauhaus eller mathandeln.",
      "Vi erbjuder samma servicenivå och precision som i alla våra salonger: klassisk herrklippning, skin fade, taper fade och skäggtrimning. Drop-in välkommen i mån av tid.",
  ],
  "herrklippning-akersberga": [
      "Tailor's Barber Åkersberga är Österåker kommuns enda specialiserade barbershop. Vi finns inne i ICA Maxi Åkersberga på Långhundravägen 9 och erbjuder professionell herrklippning, skin fade, taper fade och skäggtrimning.",
      "Oavsett om du vill ha en klassisk klippning eller en modern fade — vi levererar precision och stil. Drop-in välkommen i mån av tid, tidsbokning via BokaDirekt.",
  ],
  "skin-fade-taby": [
      "Skin fade är en av de mest tekniskt krävande klippningarna inom modern barbering. Hos Tailor's Barber Täby Centrum utförs den med maskin, bladfri övergångsteknik och rakkniv för att uppnå en helt slät, gradvis övergång från huden upp till önskad längd.",
      "Täby Centrum är vår flaggskeppssalong med Stockholmsregionens mest erfarna barberare. Skin fade är en av våra mest bokade tjänster här — kunderna återkommer regelbundet för att hålla övergångarna skarpa och väldefinierade.",
      "Resultatet: en ren, modern frisyr som lyfter hela ditt uttryck. Oavsett om du kombinerar det med en taper, en crop eller ett klassiskt herrklipp — skin fade gör skillnaden.",
  ],
  "skin-fade-arninge": [
      "Skin fade kräver precision, rätt teknik och tålmodighet — egenskaper vårt Arninge-team besitter fullt ut. Här utförs varje fade med maskin och rakkniv för en slät, gradvis övergång från ren hud upp till önskad längd på toppen.",
      "Tailor's Barber Arninge ligger i ICA Maxi-komplexet i Arninge Centrum — enkelt att nå från Täby, Vallentuna och norra Stockholm. Besökare från hela upptagningsområdet väljer oss för att det är smidigt att kombinera handling med ett besök.",
      "Ingen annan salong i Arninge specialiserar sig på skin fade med den tekniska nivå vi erbjuder. Hos oss är det barbering på riktigt.",
  ],
  "skin-fade-akersberga": [
      "Tailor's Barber Åkersberga är den enda dedikerade barbershopen i Österåker som specialiserar sig på skin fade. Vi utför varje fade med professionell maskin och rakkniv — samma teknik och standard som våra salonger i Stockholm city och Täby.",
      "Åkersberga och Österåker är en växande region med nästan 50 000 invånare. Tidigare fick du åka in till stan för en ordentlig fade. Nu finns vi här.",
      "Resultatet: en skarp, ren övergång från hud till önskad längd. Precis som du vill ha det — varje gång.",
  ],
  "drop-in": [
      "Drop-in finns på alla våra salonger i Stockholm i mån av plats. Det är ett bra alternativ när du har kort om tid och vill se om vi har en lucka.",
      "För garanterad tid rekommenderar vi att du bokar online via BokaDirekt — då slipper du vänta.",
  ],
  "herrfrisyr": [
      "Letar du inspiration till nästa klippning? Vår guide till herrfrisyrer samlar de mest populära stilarna våra barberare gör i Stockholm just nu — från klassiska business-cuts och slick back till skin fade, taper fade, textured crop och längre flow-stilar.",
      "Boka en konsultation med någon av våra barberare så hjälper vi dig hitta en herrfrisyr som passar ditt hår, ansiktsform och din vardag.",
  ],
  "herrklippning-skaggtrimning": [
      "Behöver du fräscha upp frisyren och samtidigt få skägget i ordning? Med vår kombinationsbehandling får du en klippning och skäggtrimning som sitter snyggt, håller formen och passar din stil.",
      "Vi anpassar både klippning och skäggtrim efter ditt hår, din ansiktsform och din vardag — så att helheten blir balanserad och genomarbetad.",
  ],
  "skaggtrimning": [
      "Skäggtrimning i Stockholm handlar om precision. Våra barberare formar, trimmar och linjerar ditt skägg anpassat efter din ansiktsform, din skäggväxt och din personliga stil. En skäggtrimning kostar 380 kr, och hår och skägg i samma besök kostar 680 kr.",
      "Vi jobbar med sax, maskin och rakkniv för att sätta skarpa linjer och en form som håller mellan besöken. Det är skillnaden mot en enkel trimning: vi kortar inte bara längden, vi bygger en balanserad form och avslutar med styling.",
      "Du hittar oss på fyra salonger i Stockholmsområdet: Åhléns City, Täby Centrum, Åkersberga och Arninge. Alla tar emot drop-in i mån av tid, men vi rekommenderar att du bokar online för att säkra plats hos din barberare.",
  ],

  "slatrakning": [
      "En riktig slätrakning är mer än att bara ta bort skäggstubb. Våra barberare arbetar med varm handduk, rakkniv och noggrann teknik för ett nära resultat som är skonsamt mot huden.",
      "Behandlingen passar dig som vill ha släta kinder, en ren halslinje eller en klassisk rakning utan irritation. Vi avslutar med produkter som lugnar och återfuktar huden.",
  ],
};

